import React, { useEffect, useState, useRef } from 'react';
import { checkCodeReq } from "../../features/auth/authSlice";
import { useDispatch } from "react-redux";
import { showSnackBar } from "../../components/Snackbar/Snackbar";
import { useForgotPasswordFlow } from "../../features/auth/useForgotPasswordFlow";
import { useLocation, useNavigate } from "react-router-dom";
import { NEUTRAL_CONFIRM_CODE_CSS } from "../../components/NeutralAuth/neutralCardCss";
import NeutralPortalHeader from "../../components/NeutralAuth/NeutralPortalHeader";

const ConfirmCode = ({ neutral = false }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [reqError, setReqError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const { email: reqEmail } = location.state || {};
  const flow = useForgotPasswordFlow()
  const isClient = !flow.isInternal
  const accent = flow.accent
  const accentHover = flow.accentHover
  const inputRefs = useRef([]);

  useEffect(() => {
    const handleReqError = () => {
      if (reqError && reqError.length > 0) {
        showSnackBar("error", reqError);
        setReqError('');
      }
    };
    handleReqError()
  }, [reqError])

  // Reached directly by a pasted URL, a bookmark or a new tab, this page would otherwise render a
  // submittable form with no address to submit against.
  useEffect(() => {
    if (!reqEmail) navigate(flow.forgotPasswordPath, { replace: true })
  }, [reqEmail, flow.forgotPasswordPath, navigate])

  const handleChange = (index, value) => {
    if (value.length > 1) {
      const pastedCode = value.slice(0, 6);
      const newCode = [...code];

      for (let i = 0; i < pastedCode.length && (index + i) < 6; i++) {
        if (/^\d$/.test(pastedCode[i])) {
          newCode[index + i] = pastedCode[i];
        }
      }

      setCode(newCode);

      const nextIndex = Math.min(index + pastedCode.length, 5);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
      return;
    }

    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    } else if (e.key === 'ArrowRight' && index < 5) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleConfirmCode = async () => {
    const codeReq = code.join('');

    // A partial code would still reach bcrypt.compare and spend one of the five attempts the code
    // is allowed.
    if (codeReq.length !== 6) {
      setReqError('Enter all 6 digits');
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Stated so the server checks this flow's row only: without it a portal and an internal code
      // for one address are indistinguishable, and a guess at either burns the other's budget.
      const response = await dispatch(checkCodeReq({
        email: reqEmail,
        code: codeReq,
        flowKind: flow.isInternal ? 'internal' : 'portal',
      }));

      if (response?.payload?.status === "success") {
        // The code travels in navigate state rather than the query string: it is a single-use
        // credential and has no business in the URL bar, browser history or a Referer header.
        navigate(`${flow.resetPasswordPath}?email=${encodeURIComponent(reqEmail)}`, { state: { code: codeReq } })
      } else {
        // rejectWithValue puts the backend message on payload, not on error — reading it from
        // error left every wrong code showing nothing at all.
        const message = typeof response?.payload === 'string'
          ? response.payload
          : 'Invalid or expired confirmation code'
        setReqError(message);
      }
    } catch (error) {
      console.error('Error in handleConfirmCode:', error);
      setReqError('An error occurred while processing your request.');
    }

    setIsSubmitting(false);
  };

  const handleResendCode = async () => {
    const response = await dispatch(flow.requestCodeThunk({ email: reqEmail }))

    if (!flow.requestCodeThunk.fulfilled.match(response)) {
      const message = typeof response.payload === 'string' ? response.payload : 'Failed to resend code'
      setReqError(message)
      return
    }

    // A resend replaces the code on the server, so anything already typed now belongs to a code
    // that no longer exists — submitting it would burn an attempt against the new one.
    setCode(['', '', '', '', '', ''])
    if (inputRefs.current[0]) inputRefs.current[0].focus()
    showSnackBar("success", `We resend code to ${reqEmail}`)
  }

  return (
    <>
      <style>{neutral ? NEUTRAL_CONFIRM_CODE_CSS : `
        .cc-root {
          min-height: 100vh;
          background: #0f0f0f;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'DM Sans', sans-serif;
        }
        .cc-card {
          background: #161616;
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 16px;
          padding: 48px;
          width: 100%;
          max-width: 420px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        }
        .cc-icon {
          width: 48px;
          height: 48px;
        }
        .cc-heading {
          color: #ffffff;
          font-size: 24px;
          font-weight: 600;
          margin: 0;
          text-align: center;
        }
        .cc-subtitle {
          color: rgba(255,255,255,0.45);
          font-size: 13px;
          line-height: 1.6;
          text-align: center;
          margin: 0;
        }
        .cc-inputs {
          display: flex;
          gap: 8px;
          justify-content: center;
          width: 100%;
        }
        .cc-digit {
          width: 48px;
          height: 54px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: #ffffff;
          font-size: 20px;
          font-family: 'DM Sans', sans-serif;
          text-align: center;
          outline: none;
          transition: border-color 0.2s;
        }
        .cc-digit:focus {
          border-color: ${accent};
        }
        .cc-btn {
          width: 100%;
          background: ${accent};
          color: #ffffff;
          border: none;
          border-radius: 10px;
          padding: 14px;
          font-size: 14px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s;
        }
        .cc-btn:hover {
          background: ${accentHover};
        }
        .cc-resend {
          color: rgba(255,255,255,0.45);
          font-size: 13px;
        }
        .cc-resend-link {
          color: ${accent};
          cursor: pointer;
          font-weight: 500;
          background: none;
          border: none;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          padding: 0;
        }
        .cc-resend-link:hover {
          text-decoration: underline;
        }
        .cc-link {
          color: rgba(255,255,255,0.45);
          font-size: 13px;
          text-decoration: none;
          transition: color 0.2s;
        }
        .cc-link:hover {
          color: rgba(255,255,255,0.7);
        }
      `}</style>
      <div className="cc-root">
        {neutral && <NeutralPortalHeader />}
        <div className="cc-card">
          <svg className="cc-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="10" width="40" height="28" rx="3" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
            <path d="M4 13l20 14L44 13" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <h2 className="cc-heading">Request sent successfully!</h2>
          <p className="cc-subtitle">
            We've sent a 6-digit confirmation email to {reqEmail}. Please enter the code in below box to verify your email.
          </p>
          <div className="cc-inputs">
            {code.map((digit, index) => (
              <input
                key={index}
                className="cc-digit"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                maxLength={6}
                ref={(ref) => (inputRefs.current[index] = ref)}
                autoFocus={index === 0}
              />
            ))}
          </div>
          <button className="cc-btn" onClick={() => handleConfirmCode()} disabled={isSubmitting}>
            {isSubmitting ? 'Verifying…' : 'Verify'}
          </button>
          <span className="cc-resend">
            Don't have a code?{' '}
            <button className="cc-resend-link" onClick={() => handleResendCode()}>
              Resend code
            </button>
          </span>
          <a className="cc-link" href={flow.signInPath}>Return to sign in</a>
        </div>
      </div>
    </>
  );
};

export default ConfirmCode;
